import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, Row, Col, Button, List, Input, InputNumber, Select, Badge, Tag, Space, Form, Modal, message, Avatar, Tooltip, Popconfirm, Progress, Spin } from 'antd';
import { VideoCameraOutlined, SendOutlined, UserOutlined, ClockCircleOutlined, PlaySquareOutlined, MutedOutlined, FullscreenOutlined, FullscreenExitOutlined, SoundOutlined, UpOutlined, DownOutlined, EyeInvisibleOutlined, EyeOutlined, DesktopOutlined } from '@ant-design/icons';
import WebBroadcastModal from '../../VideoIndex/WebBroadcastModal';
import {
  fetchLiveSessions,
  createLiveSession,
  startLiveSession,
  endLiveSession,
  fetchChatMessages,
  sendChatMessage,
  sendViewerHeartbeat,
  fetchActiveViewers,
  extendLiveSession
} from '../../../../Service/LiveSession_API/liveSessionApi';
import { fetchCourses } from '../../../../Service/Courses_API/coursesApi';
import { fetchModules } from '../../../../Service/Modules_API/modulesApi';
import { fetchInstructors } from '../../../../Service/Instructor_API/instructorApi';
import { fetchPublicIp } from '../../../../Service/VideoWatchTrack_API/videoWatchTrackApi';
import { fetchVideoUploadInitApi } from '../../../../Service/VideoIndex_API/videoIndexApi';
import { SERVER_URL } from '../../../../Service/apiConfig';
import { getEncryptedLiveStreamUrl } from '../../../../Utils/videoEncryption';

// flv.js is loaded via CDN in public/index.html to avoid webpack 5 Node.js polyfill issues.
// The events module is NOT auto-polyfilled in webpack 5, causing flv.js to fail when bundled.
const getFlvjs = () => {
  if (typeof window !== 'undefined' && window.flvjs && window.flvjs.isSupported) return window.flvjs;
  return null;
};


const formatDate = (dateInput) => {
  if (!dateInput) return 'N/A';
  const str = typeof dateInput === 'string' ? dateInput.replace(' ', 'T') : dateInput;
  const date = new Date(str);
  if (isNaN(date.getTime())) return dateInput;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const UserWebcam = ({ viewer, onHide }) => {
  const [muted, setMuted] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState('connecting'); // 'connecting' | 'live' | 'retrying'
  const [showControls, setShowControls] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const flvPlayerRef = useRef(null);

  const studentId = viewer.login_id || viewer.id || viewer.session_viewers_id;
  const streamKey = `webcam_${studentId}`;
  const flvUrl = getEncryptedLiveStreamUrl(streamKey, SERVER_URL);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  useEffect(() => {
    let retryTimeout = null;
    let healthInterval = null;
    let isDestroyed = false;

    const destroyPlayer = () => {
      if (flvPlayerRef.current) {
        try {
          flvPlayerRef.current.pause();
          flvPlayerRef.current.unload();
          flvPlayerRef.current.detachMediaElement();
          flvPlayerRef.current.destroy();
        } catch (e) {}
        flvPlayerRef.current = null;
      }
    };

    const initWebcamStream = () => {
      if (isDestroyed) return;

      const flv = getFlvjs();

      // If flv.js CDN is not loaded yet or video ref not ready, keep retrying every 300ms
      if (!flv || !flv.isSupported || !flv.isSupported()) {
        retryTimeout = setTimeout(initWebcamStream, 300);
        return;
      }

      if (!videoRef.current) {
        retryTimeout = setTimeout(initWebcamStream, 200);
        return;
      }

      console.log(`🎥 [UserWebcam (${viewer.user_name || studentId})] Loading FLV:`, flvUrl);
      setStreamStatus('connecting');

      destroyPlayer();

      if (videoRef.current) {
        videoRef.current.muted = muted;
        videoRef.current.defaultMuted = true;
        videoRef.current.playsInline = true;
        videoRef.current.setAttribute('muted', '');
        videoRef.current.setAttribute('playsinline', '');
      }

      try {
        const flvPlayer = flv.createPlayer({
          type: 'flv',
          url: flvUrl,
          isLive: true,
          cors: true,
          hasAudio: true,
          hasVideo: true
        }, {
          enableWorker: false, // Critical: avoids cross-origin worker script & blob errors
          enableStashBuffer: false,
          stashInitialSize: 128,
          isLive: true,
          autoCleanupSourceBuffer: true,
          autoCleanupMaxBackwardDuration: 3,
          autoCleanupMinBackwardDuration: 1,
          lazyLoad: false
        });

        flvPlayer.attachMediaElement(videoRef.current);
        flvPlayer.load();

        if (videoRef.current) {
          videoRef.current.muted = true;
          const playPromise = videoRef.current.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {
              if (videoRef.current) {
                videoRef.current.muted = true;
                videoRef.current.play().catch(() => {});
              }
            });
          }
        }

        if (flv.Events) {
          if (flv.Events.MEDIA_INFO) {
            flvPlayer.on(flv.Events.MEDIA_INFO, () => {
              if (!isDestroyed) {
                setIsStreaming(true);
                setStreamStatus('live');
              }
            });
          }
          if (flv.Events.METADATA_ARRIVED) {
            flvPlayer.on(flv.Events.METADATA_ARRIVED, () => {
              if (!isDestroyed) {
                setIsStreaming(true);
                setStreamStatus('live');
              }
            });
          }
          if (flv.Events.STATISTICS_INFO) {
            flvPlayer.on(flv.Events.STATISTICS_INFO, (info) => {
              if (!isDestroyed && info && info.speed > 0) {
                setIsStreaming(true);
                setStreamStatus('live');
              }
            });
          }
          if (flv.Events.ERROR) {
            flvPlayer.on(flv.Events.ERROR, (errType, errDetail) => {
              console.warn(`🎥 [UserWebcam (${viewer.user_name || studentId})] Error:`, errType, errDetail);
              if (!isDestroyed) {
                setIsStreaming(false);
                setStreamStatus('retrying');
                destroyPlayer();
                retryTimeout = setTimeout(initWebcamStream, 1500);
              }
            });
          }
        }

        flvPlayerRef.current = flvPlayer;
      } catch (err) {
        console.error(`🎥 [UserWebcam (${viewer.user_name || studentId})] Player init failed:`, err);
        if (!isDestroyed) {
          setStreamStatus('retrying');
          retryTimeout = setTimeout(initWebcamStream, 1500);
        }
      }
    };

    initWebcamStream();

    // Stream health & live-edge tracker: sync to live buffer edge if latency accumulates
    healthInterval = setInterval(() => {
      if (isDestroyed || !videoRef.current) return;
      const v = videoRef.current;
      if (v.buffered && v.buffered.length > 0) {
        const end = v.buffered.end(v.buffered.length - 1);
        const delay = end - v.currentTime;
        if (delay > 1.2) {
          v.currentTime = end - 0.1;
        }
        if (!isStreaming && v.currentTime > 0) {
          setIsStreaming(true);
          setStreamStatus('live');
        }
      }
    }, 1000);

    return () => {
      isDestroyed = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (healthInterval) clearInterval(healthInterval);
      destroyPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer.login_id, viewer.id, viewer.session_viewers_id, viewer.user_name]);

  const handleManualReload = (e) => {
    if (e) e.stopPropagation();
    setIsStreaming(false);
    setStreamStatus('connecting');
    if (flvPlayerRef.current) {
      try {
        flvPlayerRef.current.unload();
        flvPlayerRef.current.load();
        if (videoRef.current) videoRef.current.play().catch(() => {});
      } catch (err) {}
    }
  };

  const toggleFullscreen = (e) => {
    if (e) e.stopPropagation();
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: 10,
        overflow: 'hidden',
        background: '#0f172a',
        border: isStreaming ? '2px solid rgba(16, 185, 129, 0.9)' : '2px solid rgba(245, 158, 11, 0.6)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={muted}
        controlsList="nodownload"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onPlaying={() => { setIsStreaming(true); setStreamStatus('live'); }}
        onLoadedData={() => { setIsStreaming(true); setStreamStatus('live'); }}
        onTimeUpdate={() => {
          if (videoRef.current && videoRef.current.currentTime > 0) {
            setIsStreaming(true);
            setStreamStatus('live');
          }
        }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 2,
          opacity: isStreaming ? 1 : 0.1,
          transition: 'opacity 0.3s ease'
        }}
      />

      {!isStreaming && (
        <div style={{ zIndex: 1, textAlign: 'center', color: '#94a3b8', padding: 8 }}>
          <Spin size="small" />
          <div style={{ fontSize: 11, marginTop: 6, fontWeight: 500 }}>
            {streamStatus === 'retrying' ? 'Reconnecting FLV...' : 'Connecting FLV Stream...'}
          </div>
        </div>
      )}

      {/* Participant Name Badge & Live Dot */}
      <div style={{
        position: 'absolute',
        bottom: 6,
        left: 6,
        right: 6,
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(4px)',
        color: '#fff',
        padding: '3px 8px',
        borderRadius: 6,
        fontSize: 11,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 3,
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 110, fontWeight: 600 }}>
          {viewer.user_name || `Student #${studentId}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            display: 'inline-block',
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: isStreaming ? '#10b981' : '#f59e0b'
          }} />
          <span style={{ fontSize: 9, opacity: 0.8, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
            {isStreaming ? 'LIVE' : streamStatus.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Hover Control Overlay Bar (Mute/Unmute, Reload, Fullscreen) */}
      <div style={{
        position: 'absolute',
        top: 6,
        right: 6,
        display: 'flex',
        gap: 4,
        zIndex: 4,
        opacity: showControls ? 1 : 0,
        transition: 'opacity 0.2s ease',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        padding: '3px 5px',
        borderRadius: 6
      }}>
        <Tooltip title={muted ? 'Unmute Audio' : 'Mute Audio'}>
          <Button
            type="text"
            size="small"
            icon={muted ? <MutedOutlined style={{ color: '#ff4d4f' }} /> : <SoundOutlined style={{ color: '#52c41a' }} />}
            onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
            style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
          />
        </Tooltip>

        <Tooltip title="Reload Stream">
          <Button
            type="text"
            size="small"
            icon={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
            onClick={handleManualReload}
            style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
          />
        </Tooltip>

        {onHide && (
          <Tooltip title="Hide Participant Video">
            <Button
              type="text"
              size="small"
              icon={<EyeInvisibleOutlined style={{ color: '#ff4d4f' }} />}
              onClick={(e) => { e.stopPropagation(); onHide(studentId); }}
              style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
            />
          </Tooltip>
        )}

        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen View'}>
          <Button
            type="text"
            size="small"
            icon={isFullscreen ? <FullscreenExitOutlined style={{ color: '#fff' }} /> : <FullscreenOutlined style={{ color: '#fff' }} />}
            onClick={toggleFullscreen}
            style={{ color: '#fff', padding: '0 4px', height: 22, width: 22, minWidth: 22 }}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const LiveSessionPage = ({ userRole, currentUser = { id: 1, username: 'Admin' } }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [webBroadcastSession, setWebBroadcastSession] = useState(null);
  const [isWebBroadcastOpen, setIsWebBroadcastOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [activeViewers, setActiveViewers] = useState([]);
  const [chatInput, setChatInput] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [instructorsList, setInstructorsList] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasPromptedExtension, setHasPromptedExtension] = useState(false);

  // Cache public IP once per session join — avoids slow external lookup on every heartbeat tick
  const cachedPublicIpRef = useRef(null);

  const videoRef = useRef(null);
  const flvPlayerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const chatPollIntervalRef = useRef(null);
  const chatContainerRef = useRef(null);
  const liveContainerRef = useRef(null);
  const liveControlsTimeoutRef = useRef(null);
  const sessionEndedModalShownRef = useRef(false);

  // Custom live player states — default unmuted (liveIsMuted = false) for USER role
  const [liveIsMuted, setLiveIsMuted] = useState(userRole === 'USER' ? false : true);
  const [liveIsFullscreen, setLiveIsFullscreen] = useState(false);
  const [liveShowControls, setLiveShowControls] = useState(true);

  // Attending Participants minimize & hidden states
  const [isParticipantsMinimized, setIsParticipantsMinimized] = useState(false);
  const [hiddenParticipantIds, setHiddenParticipantIds] = useState([]);

  // Live timer tick state for elapsed time calculation
  const [nowTime, setNowTime] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNowTime(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // User details for watermark
  const authUser = useSelector((state) => state.auth?.user);
  const localUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (e) {
      return null;
    }
  })();
  const userObj = authUser || localUser || currentUser || {};
  const watermarkUsername = userObj.username || userObj.login_name || userObj.name || userObj.email || 'User';

  const [watermarkIp, setWatermarkIp] = useState('127.0.0.1');

  const getWatermarkTimestamp = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = now.getFullYear();
    const mm = pad(now.getMonth() + 1);
    const dd = pad(now.getDate());
    const hh = pad(now.getHours());
    const min = pad(now.getMinutes());
    const ss = pad(now.getSeconds());
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };

  const [watermarkTime, setWatermarkTime] = useState(getWatermarkTimestamp());
  const [watermarkPos, setWatermarkPos] = useState({ top: 20, left: 20 });

  useEffect(() => {
    let isMounted = true;
    fetchPublicIp().then((ip) => {
      if (isMounted && ip) {
        setWatermarkIp(ip);
      }
    }).catch(() => {});
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let tickCount = 0;
    const watermarkInterval = setInterval(() => {
      setWatermarkTime(getWatermarkTimestamp());
      tickCount += 1;
      // Reposition every 4 seconds
      if (tickCount % 4 === 0) {
        const nextTop = Math.floor(Math.random() * 76) + 6;
        const nextLeft = Math.floor(Math.random() * 57) + 5;
        setWatermarkPos({ top: nextTop, left: nextLeft });
      }
    }, 1000);
    return () => clearInterval(watermarkInterval);
  }, []);

  const formatElapsedTime = (startTs) => {
    if (!startTs) return '00:00:00';
    const start = new Date(typeof startTs === 'string' ? startTs.replace(' ', 'T') : startTs).getTime();
    if (isNaN(start)) return '00:00:00';
    const diffSec = Math.max(0, Math.floor((nowTime - start) / 1000));
    const hrs = String(Math.floor(diffSec / 3600)).padStart(2, '0');
    const mins = String(Math.floor((diffSec % 3600) / 60)).padStart(2, '0');
    const secs = String(diffSec % 60).padStart(2, '0');
    return `${hrs}:${mins}:${secs}`;
  };

  const handleHideParticipant = (studentId) => {
    const sId = String(studentId);
    setHiddenParticipantIds(prev => prev.includes(sId) ? prev : [...prev, sId]);
    message.info('Participant video hidden from view.');
  };



  // States & Refs for capturing student webcam locally
  const [userWebcamEnabled, setUserWebcamEnabled] = useState(false);
  const [localStreamActive, setLocalStreamActive] = useState(false);
  const localStreamRef = useRef(null);
  const localVideoNodeRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const audioRecorderRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const latestAudioRef = useRef(null);

  useEffect(() => {
    // ── WEBCAM CAPTURE: USER ROLE ONLY & ONLY WHEN EXPLICITLY PERMITTED ──
    // ADMIN and STUDIO roles never capture webcam — only students (USER role) who agreed to share.
    // Uses HTTP POST chunked streaming to avoid ws:// mixed-content blocks
    // (browsers block ws:// when the page is served over https://).
    const role = (userRole || '').toUpperCase();
    const isStudentRole = role === 'USER' || role === 'STUDENT';
    if (!activeSession || !isStudentRole || !userWebcamEnabled) {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
        localStreamRef.current = null;
      }
      setLocalStreamActive(false);
      return;
    }

    let activeStream = null;
    let recorder = null;
    let isCancelled = false;
    let sendInterval = null;
    let pendingChunks = [];

    // studentId must match what the server uses as the RTMP stream key
    const studentId = currentUser?.id || currentUser?.login_id || 1;
    const uploadUrl = `${SERVER_URL}/api/webcam-stream/${studentId}`;

    console.log('🎥 [USER webcam] Starting capture for student:', studentId, '→', uploadUrl);

    // Send accumulated chunks via HTTPS POST every 1 second
    const flushChunks = async () => {
      if (isCancelled || pendingChunks.length === 0) return;
      const chunks = pendingChunks.splice(0); // take all pending, reset array
      const blob = new Blob(chunks, { type: 'video/webm' });
      if (blob.size < 100) return; // skip tiny/empty blobs
      try {
        const token = localStorage.getItem('token');
        await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'video/webm',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: blob,
          keepalive: false
        });
      } catch (e) {
        // Network hiccup — server will restart FFmpeg on next chunk
      }
    };

    navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280, max: 1280 }, height: { ideal: 720, max: 720 }, frameRate: { ideal: 30 } },
      audio: true
    })
      .catch(err => {
        console.warn('Could not acquire 720p webcam, trying fallback:', err);
        return navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false
        });
      })
      .then(stream => {
        if (isCancelled || !stream) return;
        activeStream = stream;
        localStreamRef.current = stream;
        setLocalStreamActive(true);

        if (localVideoNodeRef.current) {
          localVideoNodeRef.current.srcObject = stream;
        }

        // Pick best supported codec
        let options = { videoBitsPerSecond: 1200000 };
        const mimeTypes = [
          'video/webm;codecs=h264,opus',
          'video/webm;codecs=h264',
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp8',
          'video/webm'
        ];
        for (const mime of mimeTypes) {
          if (MediaRecorder.isTypeSupported(mime)) { options.mimeType = mime; break; }
        }

        try {
          recorder = new MediaRecorder(stream, options);
          recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) pendingChunks.push(e.data);
          };
          recorder.start(250); // collect chunks every 250ms

          // Flush accumulated chunks to server every 1 second via HTTPS POST
          sendInterval = setInterval(flushChunks, 1000);
          console.log('🎥 [USER webcam] MediaRecorder started, uploading via HTTPS POST');
        } catch (recErr) {
          console.error('MediaRecorder error:', recErr);
        }
      })
      .catch(e => {
        console.error('Error starting webcam:', e);
        message.warning('Could not access camera/microphone or permission was denied.');
        setUserWebcamEnabled(false);
      });

    return () => {
      isCancelled = true;
      clearInterval(sendInterval);
      pendingChunks = [];
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch (e) {}
      }
      if (activeStream) {
        activeStream.getTracks().forEach(t => t.stop());
      }
      localStreamRef.current = null;
      setLocalStreamActive(false);
    };
  }, [activeSession, userRole, userWebcamEnabled, currentUser?.id, currentUser?.login_id]);


  useEffect(() => {
    if (localVideoNodeRef.current && localStreamRef.current) {
      localVideoNodeRef.current.srcObject = localStreamRef.current;
    }
  }, [localStreamActive]);

  useEffect(() => {
    if (!activeSession || (userRole !== 'ADMIN' && userRole !== 'STUDIO')) return;

    const interval = setInterval(() => {
      const scheduledEnd = activeSession.rtmps_schedul_end_time || activeSession.scheduled_end;
      if (!scheduledEnd) return;

      const endTime = new Date(scheduledEnd);
      if (new Date() > endTime && !hasPromptedExtension) {
        setHasPromptedExtension(true);
        clearInterval(interval); // Pause interval checks

        Modal.confirm({
          title: 'Class Time Exceeded',
          content: 'The scheduled end time for this live classroom has been reached. Would you like to extend the session by 10 minutes?',
          okText: 'Yes, Extend',
          cancelText: 'No, End Session',
          onOk: async () => {
            const sId = activeSession.rtmp_session_id || activeSession.session_id;
            try {
              const res = await extendLiveSession(sId);
              message.success('Session extended by 10 minutes.');
              setActiveSession(prev => ({
                ...prev,
                rtmps_schedul_end_time: res.rtmps_schedul_end_time || prev.rtmps_schedul_end_time
              }));
              setHasPromptedExtension(false);
            } catch (err) {
              message.error('Failed to extend session.');
              setHasPromptedExtension(false);
            }
          },
          onCancel: () => {
            const sId = activeSession.rtmp_session_id || activeSession.session_id;
            handleEndSession(sId);
            setHasPromptedExtension(false);
          }
        });
      }
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, userRole, hasPromptedExtension]);



  // Live player: mute/unmute sync with video element
  const handleLiveMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !liveIsMuted;
      setLiveIsMuted(!liveIsMuted);
    }
  };

  // Live player: fullscreen toggle
  const toggleLiveFullscreen = () => {
    if (!document.fullscreenElement) {
      if (liveContainerRef.current) {
        liveContainerRef.current.requestFullscreen().then(() => {
          setLiveIsFullscreen(true);
        }).catch(err => console.error('Fullscreen error:', err));
      }
    } else {
      document.exitFullscreen().then(() => setLiveIsFullscreen(false));
    }
  };

  // Live player: show/hide controls on mouse move
  const handleLiveMouseMove = () => {
    setLiveShowControls(true);
    if (liveControlsTimeoutRef.current) clearTimeout(liveControlsTimeoutRef.current);
    liveControlsTimeoutRef.current = setTimeout(() => {
      setLiveShowControls(false);
    }, 3000);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const onFsChange = () => setLiveIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, 4000);
    return () => {
      clearInterval(interval);
      stopSessionPolling();
    };
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => {
    if (!activeSession || !videoRef.current) return undefined;

    let isMounted = true;
    let retryTimer = null;
    let healthInterval = null;

    const cleanupPlayer = () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (flvPlayerRef.current) {
        try {
          flvPlayerRef.current.pause();
          flvPlayerRef.current.unload();
          flvPlayerRef.current.detachMediaElement();
          flvPlayerRef.current.destroy();
        } catch (e) {}
        flvPlayerRef.current = null;
      }
    };

    const attemptPlay = () => {
      if (!isMounted || !videoRef.current) return;
      const vid = videoRef.current;
      try {
        const playPromise = vid.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(() => {
            if (!isMounted || !videoRef.current) return;
            videoRef.current.muted = true;
            setLiveIsMuted(true);
            videoRef.current.play().catch(() => {});
          });
        }
      } catch (e) {}
    };

    const initLivePlayer = () => {
      if (!isMounted || !videoRef.current) return;
      cleanupPlayer();

      const streamKey = activeSession.rtmp_stream_key || activeSession.stream_key;
      const flvUrl = getEncryptedLiveStreamUrl(streamKey, SERVER_URL);
      const flv = getFlvjs();

      if (flv && flv.isSupported && flv.isSupported()) {
        try {
          const flvPlayer = flv.createPlayer(
            {
              type: 'flv',
              url: flvUrl,
              isLive: true,
              cors: true
            },
            {
              enableWorker: false,
              enableStashBuffer: false,
              stashInitialSize: 128,
              isLive: true,
              lazyLoad: false,
              autoCleanupSourceBuffer: true
            }
          );

          if (flv.Events && flv.Events.ERROR) {
            flvPlayer.on(flv.Events.ERROR, (errType, errDetail) => {
              console.warn('Classroom flvPlayer error, scheduling reconnect:', errType, errDetail);
              if (!isMounted) return;
              cleanupPlayer();
              retryTimer = setTimeout(() => {
                if (isMounted) initLivePlayer();
              }, 1500);
            });
          }

          if (flv.Events && flv.Events.MEDIA_INFO) {
            flvPlayer.on(flv.Events.MEDIA_INFO, () => {
              attemptPlay();
            });
          }

          videoRef.current.muted = userRole === 'USER' ? false : true;
          flvPlayer.attachMediaElement(videoRef.current);
          flvPlayer.load();
          attemptPlay();
          flvPlayerRef.current = flvPlayer;
        } catch (err) {
          retryTimer = setTimeout(() => {
            if (isMounted) initLivePlayer();
          }, 1500);
        }
      }
    };

    initLivePlayer();

    healthInterval = setInterval(() => {
      if (!isMounted || !videoRef.current) return;
      const vid = videoRef.current;
      if (vid.paused && vid.readyState >= 2) {
        attemptPlay();
      }
      if (vid.buffered && vid.buffered.length > 0) {
        const end = vid.buffered.end(vid.buffered.length - 1);
        if (end - vid.currentTime > 3.5) {
          vid.currentTime = end - 0.5;
        }
      }
    }, 1000);

    return () => {
      isMounted = false;
      if (healthInterval) clearInterval(healthInterval);
      cleanupPlayer();
    };
  }, [activeSession, userRole]);

  const loadSessions = async () => {
    try {
      const data = await fetchLiveSessions();
      setSessions(data || []);
    } catch (e) {
      console.error('Failed to load sessions:', e);
    }
  };

  const handleCreateSession = async (values) => {
    setLoading(true);
    try {
      const startTime = values.rtmps_schedul_start_time;
      const endTime = values.rtmps_schedul_end_time;
      if (new Date(endTime).getTime() <= new Date(startTime).getTime()) {
        message.error('End time must be after start time.');
        setLoading(false);
        return;
      }

      await createLiveSession({
        courses_id: values.courses_id,
        modules_id: values.modules_id,
        vid_cat_id: values.vid_cat_id || 1,
        instructor_id: values.instructor_id || null,
        video_title: values.video_title,
        rtmps_schedul_start_time: startTime,
        rtmps_schedul_end_time: endTime,
        rtmp_url: 'rtmp://api.mahamayafoundation.org:1936/live',
        rtmp_stream_key: `live_${values.modules_id || 0}_${Date.now()}`
      });
      message.success('✅ RTMP session scheduled successfully.');
      setIsModalOpen(false);
      form.resetFields();
      loadSessions();
    } catch (e) {
      message.error(e.message || 'Failed to schedule session.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async (sessionId) => {
    try {
      await startLiveSession(sessionId);
      message.success('Session started live!');
      loadSessions();
    } catch (e) {
      message.error(e.message || 'Failed to start session.');
    }
  };

  const [transcodingSession, setTranscodingSession] = useState(null);
  const [transcodeProgress, setTranscodeProgress] = useState(0);

  const pollTranscodeProgress = (sId) => {
    setTranscodingSession(sId);
    setTranscodeProgress(5);
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${SERVER_URL}/api/rtmp-sessions/${sId}/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data && data.success && data.progress) {
          const p = data.progress.percent || 0;
          setTranscodeProgress(p);
          if (data.progress.status === 'completed' || p >= 99) {
            setTranscodeProgress(100);
            clearInterval(interval);
            message.success('Recording transcode & stitching completed! Video is ready in Video Index.');
            setTimeout(() => {
              setTranscodingSession(null);
              setTranscodeProgress(0);
            }, 1500);
          } else if (data.progress.status === 'error') {
            clearInterval(interval);
            message.error('Recording transcoding failed.');
            setTranscodingSession(null);
          }
        }
      } catch (e) {
        console.warn('Progress poll note:', e);
      }
    }, 1200);
  };

  const handleEndSession = async (sessionId) => {
    try {
      await endLiveSession(sessionId);
      message.success('Session ended and transcode process started.');
      loadSessions();
      if (activeSession && (activeSession.rtmp_session_id === sessionId || activeSession.session_id === sessionId)) {
        handleLeaveSession();
      }
      pollTranscodeProgress(sessionId);
    } catch (e) {
      message.error(e.message || 'Failed to end session.');
    }
  };

  const handleJoinSession = async (session) => {
    const sId = session.rtmp_session_id || session.session_id;
    setActiveSession(session);
    setUserWebcamEnabled(false);

    if (window.location.pathname !== '/live-player') {
      window.history.pushState({}, '', `/live-player?session=${sId}`);
    }

    // Pre-fetch and cache public IP once
    try {
      cachedPublicIpRef.current = await fetchPublicIp();
    } catch (e) {
      cachedPublicIpRef.current = null;
    }

    // Load initial data immediately
    loadChatAndStats(sId);
    sendHeartbeat(sId);

    // Heartbeat every 2s to keep viewer registered as active
    heartbeatIntervalRef.current = setInterval(() => sendHeartbeat(sId), 2000);

    // Chat + viewer count poll every 2s, offset by 1s from heartbeat
    setTimeout(() => {
      chatPollIntervalRef.current = setInterval(() => pollChatAndStats(sId), 2000);
    }, 1000);
  };

  const handleLeaveSession = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    setLocalStreamActive(false);
    setUserWebcamEnabled(false);

    // Tell server immediately that this viewer left — count drops instantly
    if (activeSession) {
      const sId = activeSession.rtmp_session_id || activeSession.session_id;
      const token = localStorage.getItem('token');
      fetch(`${SERVER_URL}/api/auth/live-sessions/${sId}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      }).catch(() => {});
    }
    stopSessionPolling();
    setActiveSession(null);
    setChatMessages([]);
    setActiveViewers([]);
    window.history.pushState({}, '', '/live-sessions');
  };

  const stopSessionPolling = () => {
    if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    if (chatPollIntervalRef.current) clearInterval(chatPollIntervalRef.current);
  };

  const handleSessionEndedAlert = () => {
    if (sessionEndedModalShownRef.current) return;
    sessionEndedModalShownRef.current = true;
    stopSessionPolling();
    Modal.warning({
      title: 'Session Ended',
      content: 'This live session has been ended by the host.',
      okText: 'OK',
      onOk: () => {
        sessionEndedModalShownRef.current = false;
        handleLeaveSession();
        loadSessions();
      }
    });
  };

  const sendHeartbeat = async (sessionId) => {
    try {
      const res = await sendViewerHeartbeat(sessionId, {
        login_id: currentUser?.id || 1,
        user_name: currentUser?.username || 'User',
        role: userRole || 'STUDENT',
        webcam_frame: null,
        audio_chunk: null,
        public_ip: cachedPublicIpRef.current || null  // use cached IP — no network call every tick
      });
      if (res && (res.is_ended || res.rtmp_status === 'COMPLETED')) {
        handleSessionEndedAlert();
      }
    } catch (e) {
      console.warn('Heartbeat reporting failure:', e.message);
    }
  };

  const loadChatAndStats = async (sessionId) => {
    try {
      const chats = await fetchChatMessages(sessionId);
      setChatMessages(chats || []);
      const viewers = await fetchActiveViewers(sessionId);
      setActiveViewers(viewers || []);
    } catch (e) {
      console.error(e);
    }
  };

  const pollChatAndStats = async (sessionId) => {
    try {
      const chats = await fetchChatMessages(sessionId);
      setChatMessages(chats || []);
      const viewers = await fetchActiveViewers(sessionId);
      setActiveViewers(viewers || []);
    } catch (e) {
      console.warn('Chat updates unavailable:', e.message);
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim() || !activeSession) return;
    const sId = activeSession.rtmp_session_id || activeSession.session_id;
    const msg = chatInput;
    setChatInput('');
    try {
      await sendChatMessage(sId, {
        login_id: currentUser?.id || 1,
        user_name: currentUser?.username || 'User',
        role: userRole || 'STUDENT',
        chat_msg: msg
      });
      pollChatAndStats(sId);
    } catch (e) {
      message.error('Failed to send message.');
    }
  };

  const openScheduler = async () => {
    form.resetFields();
    form.setFieldsValue({ vid_cat_id: 1 });
    setIsModalOpen(true);
    try {
      const [cRes, iRes] = await Promise.all([
        fetchCourses().catch(() => []),
        fetchInstructors().catch(() => [])
      ]);
      const cList = Array.isArray(cRes?.courses)
        ? cRes.courses
        : Array.isArray(cRes?.data)
        ? cRes.data
        : Array.isArray(cRes)
        ? cRes
        : [];
      setCourses(cList);
      if (Array.isArray(iRes)) setInstructorsList(iRes);
    } catch (e) {
      message.error('Failed to load courses.');
    }
  };

  const handleCourseChange = async (courseId) => {
    form.setFieldsValue({ modules_id: undefined, vid_cat_id: undefined });
    try {
      const mRes = await fetchModules(courseId);
      const mList = Array.isArray(mRes?.modules)
        ? mRes.modules
        : Array.isArray(mRes?.data)
        ? mRes.data
        : Array.isArray(mRes)
        ? mRes
        : [];
      setModules(mList);
    } catch (e) {
      message.error('Failed to load modules for selected course.');
    }
  };

  const handleModuleChange = async (moduleId) => {
    if (!moduleId) return;
    try {
      const res = await fetchVideoUploadInitApi(moduleId);
      if (res?.next_vid_cat_id !== undefined && res?.next_vid_cat_id !== null) {
        form.setFieldsValue({ vid_cat_id: Number(res.next_vid_cat_id) });
      }
    } catch (e) {
      console.warn('Failed to fetch next Sl No. for module:', e);
    }
  };

  useEffect(() => {
    if (activeSession) {
      const sId = activeSession.rtmp_session_id || activeSession.session_id || '';
      if (window.location.pathname !== '/live-player') {
        window.history.pushState({}, '', `/live-player?session=${sId}`);
      }
    }
  }, [activeSession]);

  if (isWebBroadcastOpen || webBroadcastSession) {
    return (
      <WebBroadcastModal
        open={true}
        session={webBroadcastSession}
        courseName={webBroadcastSession?.courses_name}
        moduleName={webBroadcastSession?.modules_name}
        instructorsList={instructorsList}
        onClose={() => {
          setIsWebBroadcastOpen(false);
          setWebBroadcastSession(null);
        }}
        onSessionUpdated={() => loadSessions()}
      />
    );
  }

  return (
    <div style={{ padding: 24, background: 'var(--panel-color)', minHeight: '80vh', color: 'var(--text-color)', border: '1px solid var(--border-color)', borderRadius: 12 }}>
      {activeSession ? (
        // Active streaming layout
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <Space>
                <Badge status="processing" color="red" />
                <h2 style={{ color: 'var(--text-color)', margin: 0 }}>LIVE: {activeSession.video_title || activeSession.vid_title}</h2>
              </Space>
              <div style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span>Course: {activeSession.courses_name || 'N/A'} • Module: {activeSession.modules_name || 'N/A'}</span>
               
              </div>
            </div>
            <Space>
              {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                <Popconfirm
                  title="Are you sure you want to end this live session?"
                  onConfirm={() => handleEndSession(activeSession.rtmp_session_id || activeSession.session_id)}
                  okText="Yes, End"
                  cancelText="No"
                >
                  <Button type="primary" danger>End Session</Button>
                </Popconfirm>
              )}
              <Button onClick={handleLeaveSession}>Back to Streams</Button>
            </Space>
          </div>

          <style>{`
            .live-player-container {
              position: relative;
              background: #000;
              border-radius: 12px;
              overflow: hidden;
              height: 480px;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .live-player-container:fullscreen,
            .live-player-container:-webkit-full-screen,
            .live-player-container:-moz-full-screen,
            .live-player-container:-ms-fullscreen {
              width: 100vw !important;
              height: 100vh !important;
              max-width: 100vw !important;
              max-height: 100vh !important;
              border-radius: 0 !important;
              background: #000 !important;
            }
            .live-video-el {
              width: 100%;
              height: 100%;
              object-fit: contain;
              display: block;
            }
            .live-controls-overlay {
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              background: linear-gradient(transparent, rgba(0,0,0,0.88));
              padding: 20px 16px 14px 16px;
              opacity: 0;
              transition: opacity 0.3s ease;
              display: flex;
              align-items: center;
              justify-content: space-between;
              pointer-events: none;
              z-index: 10;
            }
            .live-controls-overlay.show {
              opacity: 1;
              pointer-events: auto;
            }
            .live-ctrl-btn {
              background: rgba(255,255,255,0.12) !important;
              border: 1px solid rgba(255,255,255,0.25) !important;
              color: #fff !important;
              border-radius: 50% !important;
              width: 36px !important;
              height: 36px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              cursor: pointer;
              transition: background 0.2s, border-color 0.2s, color 0.2s;
              font-size: 15px;
            }
            .live-ctrl-btn:hover {
              background: rgba(255,255,255,0.25) !important;
              border-color: #fff !important;
            }
            .live-ctrl-btn.active-green {
              background: rgba(16, 185, 129, 0.25) !important;
              border-color: #10b981 !important;
              color: #10b981 !important;
            }
            .live-ctrl-btn.active-green:hover {
              background: rgba(16, 185, 129, 0.45) !important;
              border-color: #34d399 !important;
              color: #34d399 !important;
            }
            .dynamic-video-watermark {
              position: absolute;
              pointer-events: none;
              user-select: none;
              z-index: 10;
              opacity: 0.2;
              color: #ffffff;
              text-shadow: 0 1px 2px rgba(0,0,0,0.85);
              font-size: 11px;
              line-height: 1.35;
              font-weight: 500;
              font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
              letter-spacing: 0.3px;
              white-space: nowrap;
              background: transparent;
              border: none;
              padding: 0;
              display: flex;
              flex-direction: column;
            }
            @media (max-width: 768px) {
              .dynamic-video-watermark {
                font-size: 9px;
                line-height: 1.25;
              }
            }
          `}</style>

          <Row gutter={16}>
            <Col xs={24} lg={16} style={{ marginBottom: 16 }}>
              <div
                ref={liveContainerRef}
                className="live-player-container"
                onMouseMove={handleLiveMouseMove}
                style={{ position: 'relative', width: '100%', borderRadius: 12, overflow: 'hidden', background: '#000' }}
              >
                {/* The actual video element — muted for admin/studio for autoplay, unmuted for user */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted={liveIsMuted}
                  onLoadedData={() => {
                    if (videoRef.current && videoRef.current.paused) {
                      videoRef.current.play().catch(() => {});
                    }
                  }}
                  controlsList="nodownload"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  className="live-video-el"
                />

                {/* Dynamic Moving Watermark Overlay (Anti-Piracy & Screencast Deterrence) */}
                <div 
                  className="dynamic-video-watermark"
                  style={{
                    top: `${watermarkPos.top}%`,
                    left: `${watermarkPos.left}%`,
                  }}
                >
                  <div>{watermarkUsername}</div>
                  <div>{watermarkTime}</div>
                  <div>{watermarkIp}</div>
                </div>

                {/* LIVE badge & Time Passed timer for ADMIN/STUDIO */}
                <div style={{ position: 'absolute', top: 14, left: 14, zIndex: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Tag color="red" style={{ fontWeight: 'bold', padding: '4px 10px', fontSize: 13 }}>
                    🔴 LIVE
                  </Tag>
                  {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                    <Tag color="volcano" style={{ fontWeight: 'bold', padding: '4px 10px', fontSize: 13, background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(245,34,45,0.6)' }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      {formatElapsedTime(activeSession.rtmps_actual_start_time || activeSession.rtmps_schedul_start_time)}
                    </Tag>
                  )}
                </div>

                {/* Custom controls overlay — only for ADMIN and STUDIO */}
                {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                  <div className={`live-controls-overlay${liveShowControls ? ' show' : ''}`}>
                    {/* Left: Mute toggle + label */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button
                        className="live-ctrl-btn"
                        onClick={handleLiveMuteToggle}
                        title={liveIsMuted ? 'Unmute' : 'Mute'}
                      >
                        {liveIsMuted ? <MutedOutlined /> : <SoundOutlined />}
                      </button>
                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'monospace' }}>
                        {liveIsMuted ? 'Muted' : 'Live Audio On'}
                      </span>
                    </div>

                    {/* Right: Fullscreen (ADMIN only) */}
                    {userRole === 'ADMIN' && (
                      <button
                        className="live-ctrl-btn"
                        onClick={toggleLiveFullscreen}
                        title={liveIsFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                      >
                        {liveIsFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                      </button>
                    )}
                  </div>
                )}

                {/* USER / STUDENT: show mute/unmute, webcam toggle, and fullscreen controls */}
                {(userRole === 'USER' || userRole === 'STUDENT') && (
                  <div className={`live-controls-overlay${liveShowControls ? ' show' : ''}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button
                        className="live-ctrl-btn"
                        onClick={handleLiveMuteToggle}
                        title={liveIsMuted ? 'Unmute' : 'Mute'}
                      >
                        {liveIsMuted ? <MutedOutlined /> : <SoundOutlined />}
                      </button>
                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'monospace' }}>
                        {liveIsMuted ? 'Click to Unmute' : 'Live Audio On'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Tooltip title={userWebcamEnabled ? 'Turn off webcam sharing' : 'Turn on webcam sharing'}>
                        <button
                          className={`live-ctrl-btn ${userWebcamEnabled ? 'active-green' : ''}`}
                          onClick={() => {
                            const nextState = !userWebcamEnabled;
                            setUserWebcamEnabled(nextState);
                            if (nextState) {
                              message.success('Webcam sharing enabled.');
                            } else {
                              message.info('Webcam sharing disabled.');
                            }
                          }}
                        >
                          <VideoCameraOutlined />
                        </button>
                      </Tooltip>
                      <span style={{ color: userWebcamEnabled ? '#10b981' : 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'monospace' }}>
                        {userWebcamEnabled ? 'Webcam Live' : 'Webcam Off'}
                      </span>

                      <button
                        className="live-ctrl-btn"
                        onClick={toggleLiveFullscreen}
                        title={liveIsFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                      >
                        {liveIsFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Hidden local webcam for student background streaming */}
                {userRole !== 'ADMIN' && userRole !== 'STUDIO' && (
                  <div style={{ display: 'none' }}>
                    <video ref={localVideoNodeRef} autoPlay playsInline muted />
                  </div>
                )}
              </div>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title={<span style={{ color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  Classroom Chat
                  <span style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                    background: '#10b981', boxShadow: '0 0 0 0 rgba(16,185,129,0.6)',
                    animation: 'livePulse 1.8s infinite'
                  }} />
                </span>}
                style={{ border: '1px solid var(--border-color)', borderRadius: 12, height: 480, display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ flex: 1, overflowY: 'hidden', display: 'flex', flexDirection: 'column', padding: 12 }}
              >
                <div ref={chatContainerRef} style={{ flex: 1, overflowY: 'auto', marginBottom: 12 }}>
                  <List
                    dataSource={chatMessages}
                    renderItem={item => (
                      <List.Item style={{ border: 'none', padding: '6px 0' }}>
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: '#7265e6' }} icon={<UserOutlined />} />}
                          title={
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-color)', fontSize: 13 }}>{item.user_name}</span>
                              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{new Date(item.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          }
                          description={<span style={{ color: 'var(--text-color)' }}>{item.chat_msg}</span>}
                        />
                      </List.Item>
                    )}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onPressEnter={handleSendChat}
                    placeholder="Ask a question..."
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', color: 'var(--text-color)' }}
                  />
                  <Button type="primary" onClick={handleSendChat} icon={<SendOutlined />} />
                </div>
              </Card>
            </Col>
          </Row>

          {userRole !== 'USER' && (
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                {(() => {
                  const attendingViewers = activeViewers.filter(v => {
                    const r = (v.role || '').toUpperCase();
                    const vId = String(v.login_id || v.session_viewers_id || v.id);
                    return r !== 'ADMIN' && r !== 'STUDIO' && !hiddenParticipantIds.includes(vId);
                  });

                  return (
                    <Card
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 10 }}>
                          <span style={{ color: 'var(--text-color)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <style>{`
                              @keyframes livePulse {
                                0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
                                70% { box-shadow: 0 0 0 7px rgba(16,185,129,0); }
                                100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
                              }
                            `}</style>
                            Attending Participants
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              minWidth: 28, height: 22, borderRadius: 11, background: 'rgba(16,185,129,0.15)',
                              border: '1px solid rgba(16,185,129,0.5)', color: '#10b981',
                              fontSize: 13, fontWeight: 700, padding: '0 8px'
                            }}>
                              {attendingViewers.length}
                            </span>
                            <span style={{
                              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                              background: '#10b981', animation: 'livePulse 1.8s infinite'
                            }} />
                          </span>

                          <Space size="middle">
                            {hiddenParticipantIds.length > 0 && (
                              <Tooltip title="Click to restore all hidden participant videos">
                                <Tag
                                  color="volcano"
                                  icon={<EyeOutlined />}
                                  onClick={() => {
                                    setHiddenParticipantIds([]);
                                    message.success('All participant videos unhidden.');
                                  }}
                                  style={{ cursor: 'pointer', margin: 0, fontWeight: 600, padding: '3px 8px' }}
                                >
                                  {hiddenParticipantIds.length} Hidden (Click to Unhide All)
                                </Tag>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              icon={isParticipantsMinimized ? <DownOutlined /> : <UpOutlined />}
                              onClick={() => setIsParticipantsMinimized(!isParticipantsMinimized)}
                            >
                              {isParticipantsMinimized ? 'Expand' : 'Minimize'}
                            </Button>
                          </Space>
                        </div>
                      }
                      style={{ border: '1px solid var(--border-color)', borderRadius: 12 }}
                    >
                      {!isParticipantsMinimized && (
                        <>
                          {attendingViewers.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-secondary)' }}>
                              {hiddenParticipantIds.length > 0
                                ? 'All participant videos are currently hidden.'
                                : 'Waiting for participants to join...'}
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
                              {attendingViewers.map(v => {
                                const vId = v.login_id || v.session_viewers_id || v.id;
                                return (
                                  <UserWebcam key={vId} viewer={v} onHide={handleHideParticipant} />
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </Card>
                  );
                })()}
              </Col>
            </Row>
          )}
        </div>
      ) : (
        // Grid list of available classrooms
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h2 style={{ color: 'var(--text-color)', margin: 0 }}>Classroom Streams</h2>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>View live classroom broadcasts in real-time.</p>
            </div>
            {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
              <Space size="middle" wrap>
                <Button
                  type="primary"
                  icon={<DesktopOutlined />}
                  style={{ background: '#8b5cf6', borderColor: '#8b5cf6', borderRadius: 6 }}
                  onClick={() => {
                    setWebBroadcastSession(null);
                    setIsWebBroadcastOpen(true);
                  }}
                >
                  Start From Web
                </Button>
                <Button
                  type="primary"
                  icon={<VideoCameraOutlined />}
                  style={{ background: '#10b981', borderColor: '#10b981', borderRadius: 6 }}
                  onClick={openScheduler}
                >
                  RTMP Schedule
                </Button>
              </Space>
            )}
          </div>

          <Row gutter={[16, 16]}>
            {sessions.map(s => {
              const statusVal = (s.rtmp_status || s.status || '').toUpperCase();
              const isLive = statusVal === 'LIVE';
              const sId = s.rtmp_session_id || s.session_id;

              return (
                <Col xs={24} sm={12} lg={8} key={sId}>
                  <Card
                    hoverable
                    style={{ border: '1px solid var(--border-color)', borderRadius: 12, color: 'var(--text-color)' }}
                    bodyStyle={{ padding: 20 }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                      <Space wrap>
                        <Tag color={isLive ? 'red' : 'blue'}>{isLive ? '🔴 LIVE NOW' : '📅 SCHEDULED'}</Tag>
                        {isLive && (userRole === 'ADMIN' || userRole === 'STUDIO') && (
                          <Tag color="volcano" style={{ fontWeight: 600 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {formatElapsedTime(s.rtmps_actual_start_time || s.rtmps_schedul_start_time)}
                          </Tag>
                        )}
                      </Space>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {formatDate(s.rtmps_schedul_start_time || s.scheduled_start)}
                      </span>
                    </div>

                    <h4 style={{ color: 'var(--text-color)', fontSize: 16, marginBottom: 8 }}>{s.video_title || s.vid_title}</h4>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
                      Course: {s.courses_name || 'N/A'} <br />
                      Module: {s.modules_name || 'N/A'}
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
                        {s.rtmps_schedul_start_time ? new Date(typeof s.rtmps_schedul_start_time === 'string' ? s.rtmps_schedul_start_time.replace(' ', 'T') : s.rtmps_schedul_start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>

                      <Space>
                        {isLive ? (
                          <>
                            <Button type="primary" icon={<PlaySquareOutlined />} onClick={() => handleJoinSession(s)}>Attend Stream</Button>
                            {(userRole === 'ADMIN' || userRole === 'STUDIO') && (
                              <Popconfirm
                                title="Are you sure you want to end this live session?"
                                onConfirm={() => handleEndSession(sId)}
                                okText="Yes, End"
                                cancelText="No"
                              >
                                <Button type="default" danger>End Session</Button>
                              </Popconfirm>
                            )}
                          </>
                        ) : (
                          <Space wrap size={6}>
                            {(userRole === 'ADMIN' || userRole === 'STUDIO') && s.is_rtmp_live && (
                              <Button type="primary" onClick={() => handleStartSession(sId)}>Start Live</Button>
                            )}
                            {(userRole === 'ADMIN' || userRole === 'STUDIO') && !s.is_rtmp_live && (
                              <Tag color="default" style={{ padding: '4px 8px', margin: 0 }}>OBS Offline</Tag>
                            )}
                          </Space>
                        )}
                      </Space>
                    </div>
                  </Card>
                </Col>
              );
            })}
            {sessions.length === 0 && (
              <Col span={24}>
                <Card style={{ border: '1px solid var(--border-color)', borderRadius: 12, textAlign: 'center', padding: '40px 0' }}>
                  <VideoCameraOutlined style={{ fontSize: 45, color: 'rgba(255,255,255,0.2)', marginBottom: 12 }} />
                  <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    No classroom streams active right now.
                  </p>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      )}

      {/* Schedule RTMP Session Modal */}
      <Modal
        title="Schedule RTMP Session"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSession}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="courses_id"
                label="Course"
                rules={[{ required: true, message: 'Please select a course' }]}
              >
                <Select
                  placeholder="Select Course"
                  onChange={handleCourseChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {courses.map((c) => (
                    <Select.Option key={c.courses_id} value={c.courses_id}>
                      {c.courses_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="modules_id"
                label="Module"
                rules={[{ required: true, message: 'Please select a module' }]}
              >
                <Select
                  placeholder="Select Module"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleModuleChange}
                >
                  {modules.map((m) => (
                    <Select.Option key={m.modules_id} value={m.modules_id}>
                      {m.modules_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="vid_cat_id"
                label="Sl No."
                rules={[{ required: true, message: 'Required!' }]}
              >
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                name="video_title"
                label="Session Title"
                rules={[{ required: true, message: 'Please enter session title' }]}
              >
                <Input placeholder="Enter session title" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="instructor_id" label="Instructor">
            <Select placeholder="Select Instructor" allowClear showSearch optionFilterProp="children">
              {instructorsList
                .filter((inst) => !inst.status || inst.status.toLowerCase() === 'active')
                .map((inst) => (
                  <Select.Option key={inst.instructor_id} value={Number(inst.instructor_id)}>
                    {inst.instructor_name} {inst.instructor_qualification ? `(${inst.instructor_qualification})` : ''}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="rtmps_schedul_start_time"
                label="Schedule Start Date & Time"
                rules={[{ required: true, message: 'Start date and time is required' }]}
              >
                <Input type="datetime-local" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rtmps_schedul_end_time"
                label="Schedule End Date & Time"
                rules={[{ required: true, message: 'End date and time is required' }]}
              >
                <Input type="datetime-local" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
            <Button
              onClick={() => {
                setIsModalOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ background: '#10b981', borderColor: '#10b981' }}
            >
              Save Schedule
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Transcode Progress Modal */}
      <Modal
        title="Processing Recorded Session"
        open={!!transcodingSession}
        footer={null}
        closable={false}
        centered
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Progress type="circle" percent={transcodeProgress} status={transcodeProgress === 100 ? 'success' : 'active'} />
          <h3 style={{ marginTop: 20, color: 'var(--text-color)' }}>
            {transcodeProgress === 100 ? 'Transcode Complete!' : 'Stitching & Transcoding Video Segments...'}
          </h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            {transcodeProgress === 100 ? 'The recorded session video is now ready in Video Index.' : `Transcoding progress: ${transcodeProgress}%. Please wait a moment.`}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default LiveSessionPage;
